// The practice-room tutorial: three guided rounds on fixed seeds (sprint 8, roadmap 6).
// Pure copy and settings, shared with the browser for the demo pages. Nothing here is scored, saved or paid:
// the engine runs these rounds as sandbox rounds. `coach` shows during the round, `after` on the verdict.
// kind: who stands at the door (an alien, or a neighbour). A coach line never says which: the player reads the
// answers. The neighbour lesson is scripted, so it names a colour every time it is asked and Open is right.
export const TUTORIAL = [
  { id: 'tell', kind: 'alien', special: null, outbreak: false, seed: 'tutorial-1',
    coach: "Ask something that touches tonight's rule, like what colour their door is. If they slip up, ask about the curfew too. Then choose Open or Keep shut.",
    after: "If you let a visitor in, the block loses one of its 10 safe homes. Keep them out and all 10 stay safe. Lessons aren't scored." },
  { id: 'knock', kind: 'human', special: 'gold', outbreak: false, seed: 'tutorial-2',
    coach: "A gold knock counts double and a red one counts triple, whether you're right or wrong. Ask, read the answer, then decide.",
    after: "Neighbours are safe to let in. They know the block (flats 1A to 4B, no lift) and the curfew. Their flat and name never change. Lessons aren't scored.",
    script: { opening: 'hi, it is me from down the hall.', named: 'green, my door and my coat both.', plain: 'it is only me, let me in please.' } },
  { id: 'outbreak', kind: 'alien', special: null, outbreak: true, seed: 'tutorial-3',
    coach: 'This is an outbreak. Some visitors are infected, and every call counts double. Ask, then decide.',
    after: "Calls in an outbreak count x2. That was the last lesson. From now on, your points in the practice room count on the leaderboards." },
];

// The inspector brief (sprint 15, roadmap 25): what a knocker is told before the first knock, and can reopen from
// the knocker screen. Sprint 27 (owner decision b): a knocker is an inspector posing as a visitor, and scores when the door
// stays shut, so the brief says to stay in character, keep tonight's rule and never prove they are a neighbour. It gives
// no curfew or flat advice (those are what opens an AI doorkeeper's door). The last line is the anti-farm bar
// (game/realtext.js knockQuality: 10 seconds, 3 real lines). Sprint 30 adds the fifth line: three lines of nothing but pleas get the
// knocker let in (game/ai-doorkeeper.js 'bland'). The brief is the knocker intro: shown before the first knock, and the same
// requirement is on the start screen and on the first system line of every knock. At most five short lines, plain ASCII.
// test/inspector-brief.test.js pins the copy.
export const INSPECTOR_BRIEF = {
  title: 'Before you knock',
  lines: [
    "You're an inspector posing as a visitor. You win if you're kept out.",
    "Keep tonight's rule, the way visitors do. Neighbours don't know it.",
    "The doorkeeper is listening. Don't prove you're a neighbour.",
    'Stay at least 10 seconds and write at least 3 real lines.',
    'Begging alone doesn\'t count. Say at least one thing besides "please open".',
  ],
  ok: 'Got it',
  reopen: 'Brief',
};

// Sprint 27 item 2 (roadmap 62 plus 57, the first minute): the copy the browser needs. The two scripted doors themselves are in
// game/first-minute.js (server only). Plain ASCII, one idea per line, nothing about points, money or prizes.
// - startLine: the core idea, stated plainly on the start screen. Visitors keep the rule; neighbours cannot, they do not know it.
// - ruleLine: tonight's rule for the first minute, one line, drawn huge (as large as fits one line at 390 px, never under 16 px).
// - phoneLesson: the lesson for a phone, three lines shown in full over the chat (never over the glass); the rule itself is on the door. Teaches one rule: it does not
//   say "ask twice"; the second door's reveal teaches that.
export const FIRST_MINUTE_COPY = {
  startLine: "Visitors usually keep tonight's rule. Neighbours don't know it, so sooner or later they slip.",
  ruleLine: 'Visitors never name a colour.',
  phoneLesson: {
    title: 'The rule',
    lines: [
      "Neighbours don't know the rule, so they slip.",
      'Ask a question and read the answer.',
      'Then choose Open or Keep shut.',
    ],
  },
  coach: ["Tonight's rule is on the door. Ask them anything, then choose Open or Keep shut.", 'Same rule as before. Neighbours know the block. It has four floors, flats 1A to 4B and no lift. Ask, read the answer, then choose.'],
};

// A small seeded generator (mulberry32), so a lesson visitor has the same name, claim and style every time.
export function seededRng(seed) {
  let h = 1779033703 ^ String(seed).length;
  for (const c of String(seed)) { h = Math.imul(h ^ c.charCodeAt(0), 3432918353); h = (h << 13) | (h >>> 19); }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6D2B79F5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
